import { get, isArray, isNil, isObject, template } from 'lodash';

import { abbrNum, addThousandsSeparator } from './common';
import { isInteger } from './general';
import { findMetricValue } from '../metrics';

import type { HelperAbbrNumOptions } from 'types/Helper.type';

type PriceRangeAsTuple = [number | string, number | string];

type PriceRangeAsObject = {
  customTemplate: string
} & {
  [key: string]: number | string
};

type PriceRange = PriceRangeAsTuple | PriceRangeAsObject;

type RenderPriceOptions = {
  round?: boolean,
  separateThousands?: boolean,
  abbreviate?: HelperAbbrNumOptions & {
    allowDecimalPlaces?: boolean
  }
};

function roundToNearest10(num: number): number {
  // if num is <= 5, it will be rounded to 0. in that case, just return the original `num`
  return num > 5 ? Math.round(num / 10) * 10 : num;
}

export function getAbbreviatedPriceRecommendationRange(minPricePerPost: number, maxPricePerPost: number): string | undefined | null {
  if (isNaN(minPricePerPost) || isNaN(maxPricePerPost)) {
    return undefined;
  }

  const minPrice = roundToNearest10(minPricePerPost ?? 0);
  const maxPrice = roundToNearest10(maxPricePerPost ?? 0);

  if (minPrice !== maxPrice) {
    return `${abbrNum(minPrice, { decimal: 1 }, true)} - ${abbrNum(maxPrice, { decimal: 1 }, true)}€`;
  }

  return minPrice !== 0 ? `${abbrNum(minPrice, { decimal: 1 }, true)}€` : undefined;
}

export function getPriceRecommendationRange(minPricePerPost: number, maxPricePerPost: number): string | undefined | null {
  if (isNaN(minPricePerPost) || isNaN(maxPricePerPost)) {
    return undefined;
  }

  const minPrice = roundToNearest10(minPricePerPost ?? 0);
  const maxPrice = roundToNearest10(maxPricePerPost ?? 0);

  if (minPrice !== maxPrice) {
    return `${abbrNum(minPrice)}-${abbrNum(maxPrice)}€`;
  }

  return minPrice !== 0 ? `${abbrNum(minPrice)}€` : undefined;
}

export const getPriceRangeFromChannelArray = (channels: Array<any>, channel: string = 'instagram'): [number, number] | undefined | null => {
  // Channels are in array format when they're not coming from search endpoint
  const metricsArray = (channels.find(influencerChannel => influencerChannel.type === channel) || {}).metrics;

  if (isNil(metricsArray)) {
    return null;
  }

  const minPricePerPost = findMetricValue<number>(metricsArray, 'min_price_per_post');
  const maxPricePerPost = findMetricValue<number>(metricsArray, 'max_price_per_post');

  if (isNil(minPricePerPost) || isNil(maxPricePerPost)) {
    return null;
  }

  return [minPricePerPost, maxPricePerPost];
};

export const getPriceRangeFromChannelObject = (channels: {}, channel: string = 'instagram'): [number, number] | undefined | null => {
  const metrics: any = get(channels, `${channel}.metrics`, {});

  if (isNil(metrics) || isNil(metrics.minPricePerPost) || isNil(metrics.maxPricePerPost)) {
    return null;
  }

  return [metrics.minPricePerPost, metrics.maxPricePerPost];
};

export const getPriceRecommendationRangeFromChannels = (channels: {} | Array<any>, channel: string = 'instagram'): [number, number] | undefined | null => {
  if (isArray(channels)) {
    return getPriceRangeFromChannelArray(channels, channel);
  }

  return getPriceRangeFromChannelObject(channels, channel);
};

const applyOptions = (value: number | string, options?: RenderPriceOptions): number | string => {
  let result = value;
  const hasAbbreviation = options && !isNil(options.abbreviate);
  const hasSeparationOfThousands = isNil(options) || (options && options.separateThousands !== false);

  if (typeof result === 'number' && !isInteger(result)) {
    result = parseFloat(result.toFixed(2));
  }

  if (options && options.round && typeof result === 'number') {
    result = roundToNearest10(result);
  }

  if (hasSeparationOfThousands && !hasAbbreviation) {
    result = addThousandsSeparator(result);
  }

  if (hasAbbreviation) {
    const abbrOptions: HelperAbbrNumOptions & {
      allowDecimalPlaces?: boolean
    } | undefined | null = options && isObject(options.abbreviate) ? options.abbreviate : undefined;
    const allowDecimalPlaces = abbrOptions ? abbrOptions.allowDecimalPlaces : undefined;
    result = abbrNum(result, abbrOptions, allowDecimalPlaces);
  }

  return result;
};

export const renderPrice = (
  value?: number | string | null,
  options?: RenderPriceOptions & {
    defaultValue?: string | null
  }
): string => {
  const defaultValue = options && !isNil(options.defaultValue) ? options.defaultValue : '0€';

  if (!value) {
    return defaultValue;
  }

  const price = applyOptions(value, options);

  if (!price) {
    return defaultValue;
  }

  const noOfDecimalPlaces = (String(price).split('.')[1] || '').length;

  return noOfDecimalPlaces == 1 ? `${price}0€` : `${price}€`;
};

export const renderPriceRange = (range: PriceRange, options?: RenderPriceOptions): string => {
  if (isArray(range)) {
    const prices = range.map(price => applyOptions(price, options));
    return `${prices[0]}-${prices[1]}€`;
  }

  const { customTemplate, ...rest } = range;
  const templateValues = Object.keys(rest).reduce(
    (acc, val) => {
      acc[val] = applyOptions(rest[val], options);
      return acc;
    },
    {}
  );

  return template(customTemplate)(templateValues);
};
